import { useCallback } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNumber from "lodash-es/isNumber"
import { useUpdateEffect } from "react-use"

import {
    useCoachingChatSocketEmitter as useSockerEmitter,
    useCoachingChatSocketReceiver as useSocketReceiver
} from "../api"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatSocketEmitterEventName as ESockerEmitterEventName,
    ECoachingChatSocketListenerEventName as ESocketListenerEventName,
    type TCoachingChatSocketReadyEventResponse as TConversationReadyEventResponse,
    type TCoachingChatSocketJoinConversationEventPayload as TJoinConversationEventPayload,
    type _TCoachingChatHookSharedParams as _TParams
} from "../types"
import { logCoachingChatState as logChatState } from "../utils/coaching-chat.diagnostics"

function useCoachingChatConversationJoiner({ entityId, entityMode, wasChatCompleted }: _TParams): void {
    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const apiMode: EConversationApiMode = useConversationApiMode()

    const isSocketMode: boolean = eq(apiMode, EConversationApiMode.Sockets)

    const isFullMode: boolean = eq(entityMode, EEntityMode.Full)

    const shouldRunBasedOnStatus: boolean = includes<EConversationStatus>(
        [EConversationStatus.Created, EConversationStatus.Connected, EConversationStatus.InProgress], // inProgress for api switch on started conversation
        conversationStatus
    )

    const { emit }: ReturnType<typeof useSockerEmitter<TJoinConversationEventPayload>> =
        useSockerEmitter<TJoinConversationEventPayload>(ESockerEmitterEventName.JoinConversation)

    const handler: (resp: TConversationReadyEventResponse) => void = useCallback(
        (resp: TConversationReadyEventResponse): void => {
            setConversationApiMode(EConversationApiMode.Sockets)

            if (isNumber(resp.conversation_id)) conversationDispatcher.changeStatus(EConversationStatus.InProgress)
        },
        [conversationDispatcher]
    )

    useSocketReceiver<TConversationReadyEventResponse>({
        isEnabled: isSocketMode && shouldRunBasedOnStatus && !wasChatCompleted && isFullMode,
        event: ESocketListenerEventName.Ready,
        handler
    })

    useUpdateEffect((): void => {
        const canJoin: boolean =
            isSocketMode && isFullMode && !wasChatCompleted && shouldRunBasedOnStatus && isNumber(conversationId)

        // Named so the deciding gate survives the console's collapsed preview — it only shows the
        // first few fields, and "willEmit: false" on its own says nothing about why.
        const blockedBy: string | null = canJoin
            ? null
            : !isSocketMode
              ? "notSocketMode"
              : !isFullMode
                ? "notFullMode"
                : wasChatCompleted
                  ? "chatAlreadyCompleted"
                  : !shouldRunBasedOnStatus
                    ? `statusNotJoinable:${conversationStatus}`
                    : "noConversationId"

        // The server only sends `history` in reply to cm_join. No join means a live socket that
        // never receives the conversation, and a feed that renders empty. Note this is an
        // update-effect: if none of the gates change after mount (chat state survives SPA
        // navigation), it never runs at all and nothing is logged for that visit either.
        logChatState("join", {
            entityId,
            willEmit: canJoin,
            blockedBy,
            conversationStatus,
            conversationId,
            isSocketMode,
            isFullMode,
            wasChatCompleted,
            isStatusJoinable: shouldRunBasedOnStatus
        })

        if (!canJoin) return void 0

        void emit({ conversation_id: conversationId })

        return void 0
    }, [shouldRunBasedOnStatus, isSocketMode, conversationId, isFullMode, wasChatCompleted])
}

export { useCoachingChatConversationJoiner }
